import { haveValue } from "./common";

// Shared resolver for per-participant effort-rate settings. Mirrors the
// backend semantics in uno-backend/helper/effortRateData.js (`resolveEffortRate`):
// a canonical `participants` map is authoritative — a participant with no
// entry uses the caller's normal fallback, never another participant's
// settings. Only documents without a map fall back to legacy
// `tenant` (ordinary participant) or `guarantor[personId]` (guarantor).
// Nullish fallback is used throughout so a saved zero stays a valid setting.
const readKey = (container, key) => {
  if (container == null || key == null) return undefined;
  const id = String(key);
  if (typeof container.get === "function") {
    try {
      return container.get(id);
    } catch {
      return undefined;
    }
  }
  return container[id];
};

export const resolveEffortRateSettings = (
  data,
  personId,
  isGuarantor = false
) => {
  if (data == null || personId == null) return {};
  if (data.participants != null) {
    return readKey(data.participants, personId) || {};
  }
  return (isGuarantor ? readKey(data.guarantor, personId) : data.tenant) || {};
};

export const resolveEffortRateType = (
  data,
  personId,
  { isGuarantor = false, fallback = "tenant" } = {}
) =>
  resolveEffortRateSettings(data, personId, isGuarantor)?.effort_rate_type ??
  fallback;

export const resolveEffortRatePercentage = (
  data,
  personId,
  { isGuarantor = false, fallback } = {}
) =>
  resolveEffortRateSettings(data, personId, isGuarantor)
    ?.effort_rate_percentage ?? fallback;

// A participant's own saved settings, including the backend's resolved
// `effort_rate_settings` when present. Returns {} when no override is saved.
export const resolveParticipantEffortSettings = (profile, participant) => {
  if (!participant) return {};
  const personId = participant.id ?? participant.tenant_info?._id;
  if (participant.effort_rate_settings) return participant.effort_rate_settings;
  if (personId == null) return {};
  return resolveEffortRateSettings(
    profile?.effort_rate_data,
    personId,
    !!participant.is_guarantor
  );
};

// Whether an account participates as a guarantor on the shared profile's
// application. Used to pick the legacy fallback branch for records that
// have not been migrated to the `participants` map yet.
export const isApplicationGuarantor = (profile, personId) => {
  if (personId == null) return false;
  const participants = profile?.application_id?.participants;
  if (!Array.isArray(participants)) return false;
  return participants.some(
    (p) =>
      !p?.is_deleted &&
      String(p?.id ?? p?.tenant_info?._id ?? "") === String(personId) &&
      !!p?.is_guarantor
  );
};

// Percentage fallback chain shared by capacity readers: saved setting,
// otherwise the caller's workspace default. Zero is preserved via haveValue.
export const resolveEffortRatePercentageWithDefault = (
  data,
  personId,
  { isGuarantor = false, defaultEffortRate = 35 } = {}
) => {
  const saved = resolveEffortRatePercentage(data, personId, { isGuarantor });
  if (haveValue(saved)) return saved;
  return defaultEffortRate;
};
